import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
    DropdownMenuItem,
    DropdownMenuPortal,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger
} from 'src/@/components/ui/dropdown-menu';
import Iconify from 'src/components/Iconify';
import { Archive } from 'lucide-react';
import chatsArquivoService from 'src/services/chatsArquivoService';
import { getDivisoes } from 'src/redux/slices/chat-arquivo';
import { removeChatFromList } from 'src/redux/slices/chat';
import { verifyRules } from 'src/utils/functions';
import useAuth from 'src/hooks/useAuth';
import { getCorDivisao } from './ChatsArquivadas.design';

/**
 * Submenu "Arquivar em..." para o menu de contexto da lista de conversas.
 * As divisões são carregadas sob demanda — quem nunca abre o menu não paga a request.
 * `iconBox` liga o visual com caixinha de ícone (padrão dos menus do ChatHeader);
 * `onArquivado` permite ao chamador reagir ao sucesso (ex.: fechar o chat aberto).
 */
export default function ArquivarSubMenu({ chat, iconBox = false, onArquivado }) {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation('chat');
    const { user } = useAuth();
    const divisoes = useSelector((state) => state.chatArquivo.divisoes);
    const [carregou, setCarregou] = useState(false);

    // Gate pela rule do token, não por `permissoes` do slice: `permissoes` só é
    // preenchido depois do primeiro getDivisoes, e o fetch é disparado pela
    // abertura deste submenu — usar o slice aqui trancaria o menu para sempre.
    if (!verifyRules(user?.rules, 'arquivo.ver')) return null;

    const handleAbrir = () => {
        if (carregou || divisoes.length) return;
        setCarregou(true);
        dispatch(getDivisoes());
    };

    const handleArquivar = async (divisao) => {
        try {
            await chatsArquivoService.arquivar(`${chat._id}`, `${divisao._id}`);
            dispatch(removeChatFromList({ chatId: chat._id, status: chat.statusRoom }));
            enqueueSnackbar(t('archive.menu.archived', 'Conversa arquivada em {{name}}', { name: divisao.nome }), {
                variant: 'success',
                autoHideDuration: 3000,
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' }
            });
            onArquivado?.();
        } catch (error) {
            // 409: o webhook mexeu na conversa entre a leitura e a escrita. Não é
            // falha do usuário — a lista dele é que está desatualizada.
            const conflito = error?.conflito === true;
            enqueueSnackbar(error?.mensagem || t('archive.menu.errorArchiving', 'Não foi possível arquivar a conversa'), {
                variant: conflito ? 'warning' : 'error',
                autoHideDuration: conflito ? 6000 : 3000,
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' }
            });
        }
    };

    return (
        <DropdownMenuSub>
            <DropdownMenuSubTrigger
                onPointerEnter={handleAbrir}
                onFocus={handleAbrir}
                className={iconBox ? 'cursor-pointer gap-2.5' : undefined}
            >
                {iconBox && (
                    <div className="w-7 h-7 rounded-lg bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                        <Archive className="w-3.5 h-3.5 text-amber-500" />
                    </div>
                )}
                <span className={iconBox ? 'text-sm' : undefined}>{t('archive.menu.title', 'Arquivar em')}</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
                <DropdownMenuSubContent>
                    {!divisoes.length ? (
                        <DropdownMenuItem disabled>
                            <span className="text-xs text-gray-400">{t('archive.menu.empty', 'Nenhuma divisão criada')}</span>
                        </DropdownMenuItem>
                    ) : (
                        divisoes.map((divisao) => {
                            const cor = getCorDivisao(divisao.cor_hex);
                            return (
                                <DropdownMenuItem
                                    key={`${divisao._id}`}
                                    onClick={() => handleArquivar(divisao)}
                                >
                                    <Iconify
                                        icon={divisao.icone || 'eva:folder-outline'}
                                        width={14}
                                        className={`mr-2 ${cor.text}`}
                                    />
                                    <span>{divisao.nome}</span>
                                </DropdownMenuItem>
                            );
                        })
                    )}
                </DropdownMenuSubContent>
            </DropdownMenuPortal>
        </DropdownMenuSub>
    );
}
